import React, { useMemo, useState } from 'react';
import { useAdminProcesses } from '../../hooks/useAdminProcesses.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const ProcessesSection = ({ onNotify }) => {
  const { items, loading, error, refresh, saveProcess, deleteProcess } = useAdminProcesses();
  const [search, setSearch] = useState('');
  const [editorState, setEditorState] = useState({ open: false, record: null, saving: false });

  useLucideIcons([items.length, search, editorState.open]);

  const filteredItems = useMemo(() => {
    if (!search) {
      return items;
    }
    const query = search.toLowerCase();
    return items.filter((process) => {
      const name = process.nombre?.toLowerCase() || '';
      const description = process.descripcion?.toLowerCase() || '';
      return name.includes(query) || description.includes(query);
    });
  }, [items, search]);

  const handleOpenModal = (record = null) => {
    setEditorState({
      open: true,
      saving: false,
      record: record
        ? { ...record }
        : {
          nombre: '',
          descripcion: '',
          tipo: 'articulos',
          es_warmer: false,
          es_beta: false,
          es_azucar_glass: false,
          activo: true
        }
    });
  };

  const handleSave = async (values) => {
    setEditorState((prev) => ({ ...prev, saving: true }));
    try {
      await saveProcess(values);
      setEditorState({ open: false, record: null, saving: false });
      refresh();
      onNotify?.({
        type: 'success',
        message: values?.id ? 'Proceso actualizado correctamente.' : 'Proceso creado correctamente.'
      });
    } catch (err) {
      setEditorState((prev) => ({ ...prev, saving: false }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar el proceso.' });
    }
  };

  const handleDelete = async (record) => {
    const confirmed = window.confirm(`¿Eliminar el proceso "${record.nombre}"?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteProcess(record.id);
      refresh();
      onNotify?.({ type: 'success', message: 'Proceso eliminado.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo eliminar el proceso.' });
    }
  };

  const closeModal = () => {
    setEditorState({ open: false, record: null, saving: false });
  };

  return (
    <section id="processes-section" className="admin-section active">
      <div className="section-header">
        <h2 className="section-title">Procesos</h2>
        <div className="section-actions">
          <div className="search-container">
            <span className="search-icon"><i data-lucide="search" /></span>
            <input
              type="text"
              className="form-input"
              placeholder="Buscar procesos..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search ? (
              <button type="button" className="btn-secondary" onClick={() => setSearch('')}>
                Limpiar
              </button>
            ) : null}
          </div>
          <button className="btn-primary" type="button" onClick={() => handleOpenModal()}>
            <span className="btn-icon"><i data-lucide="plus" /></span>
            Nuevo Proceso
          </button>
        </div>
      </div>
      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando procesos...</div>
          </div>
        ) : (
          <table className="data-table" id="processes-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Warmer</th>
                <th>Beta</th>
                <th>Azucar Glass</th>
                <th>Artículos vinculados</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                    <div className="empty-state-text">{error}</div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="settings" /></div>
                    <div className="empty-state-text">No hay procesos configurados.</div>
                    <div className="empty-state-subtext">Crea un proceso para comenzar.</div>
                  </td>
                </tr>
              ) : (
                filteredItems.map((process) => (
                  <tr key={process.id}>
                    <td>
                      <div className="process-info">
                        <strong>{process.nombre}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="status-badge status-neutral">
                        {process.tipo === 'recetas' ? 'Recetas' : 'Artículos'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${process.es_warmer ? 'status-active' : 'status-inactive'}`}>
                        {process.es_warmer ? 'Sí' : 'No'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${process.es_beta ? 'status-active' : 'status-inactive'}`}>
                        {process.es_beta ? 'Sí' : 'No'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${process.es_azucar_glass ? 'status-active' : 'status-inactive'}`}>
                        {process.es_azucar_glass ? 'Sí' : 'No'}
                      </span>
                    </td>
                    <td><span className="process-count">{process.articulos_count || 0}</span></td>
                    <td>
                      <span className={`status-badge ${process.activo ? 'status-active' : 'status-inactive'}`}>
                        {process.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn edit" type="button" onClick={() => handleOpenModal(process)}>
                          <i data-lucide="edit" />
                        </button>
                        <button className="action-btn delete" type="button" onClick={() => handleDelete(process)}>
                          <i data-lucide="trash-2" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {editorState.open ? (
        <ProcessModal state={editorState} onClose={closeModal} onSubmit={handleSave} />
      ) : null}
    </section>
  );
};

const ProcessModal = ({ state, onClose, onSubmit }) => {
  const [formValues, setFormValues] = useState(() => ({
    ...state.record,
    nombre: state.record?.nombre || '',
    descripcion: state.record?.descripcion || '',
    tipo: state.record?.tipo || 'articulos',
    es_warmer: state.record?.es_warmer || false,
    es_beta: state.record?.es_beta || false,
    es_azucar_glass: state.record?.es_azucar_glass || false,
    activo: state.record?.activo ?? true
  }));

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({ ...formValues, id: state.record?.id || null });
  };

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content" style={{ maxWidth: '520px' }}>
        <div className="modal-header">
          <h3>{state.record?.id ? 'Editar Proceso' : 'Nuevo Proceso'}</h3>
          <button className="modal-close" type="button" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <form className="modal-body form-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Nombre</label>
            <input
              type="text"
              className="form-input"
              name="nombre"
              value={formValues.nombre}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Descripción</label>
            <textarea
              className="form-input"
              name="descripcion"
              value={formValues.descripcion}
              onChange={handleChange}
              rows={3}
              placeholder="Describe el proceso..."
            />
          </div>
          <div className="form-group">
            <label className="form-label">Tipo</label>
            <select
              className="form-select"
              name="tipo"
              value={formValues.tipo}
              onChange={handleChange}
            >
              <option value="articulos">Artículos</option>
              <option value="recetas">Recetas</option>
            </select>
          </div>
          <div className="form-group checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="es_warmer"
                checked={formValues.es_warmer}
                onChange={handleChange}
              />
              <span className="checkmark" />
              <span className="checkbox-label">Proceso de warmer</span>
            </label>
          </div>
          <div className="form-group checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="es_beta"
                checked={formValues.es_beta}
                onChange={handleChange}
              />
              <span className="checkmark" />
              <span className="checkbox-label">Proceso beta</span>
            </label>
          </div>
          <div className="form-group checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="es_azucar_glass"
                checked={formValues.es_azucar_glass}
                onChange={handleChange}
              />
              <span className="checkmark" />
              <span className="checkbox-label">Proceso Azucar Glass</span>
            </label>
          </div>
          <div className="form-group checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="activo"
                checked={formValues.activo}
                onChange={handleChange}
              />
              <span className="checkmark" />
              <span className="checkbox-label">Proceso activo</span>
            </label>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" type="button" onClick={onClose} disabled={state.saving}>
              Cancelar
            </button>
            <button className="btn-primary" type="submit" disabled={state.saving}>
              {state.saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProcessesSection;
